import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, MessageCircle, Mail, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only utility to recover a paying customer's stuck ₹99 session.
 *
 * Usage: Guruji logs in as admin, then visits /admin/poocho-recover.
 * Enters the phone number the customer used in the form. Optionally overrides
 * WhatsApp/email delivery target (useful when customer's phone == Twilio FROM
 * number). Backend regenerates the answer and re-delivers.
 *
 * Feb 2026 — created after a real ₹99 payment got stuck 10+ min in
 * status=generating (no answer on screen, no WhatsApp, no email).
 */
export default function PoochoRecoverAdminPage() {
  const [phone, setPhone] = useState('');
  const [overridePhone, setOverridePhone] = useState('');
  const [overrideEmail, setOverrideEmail] = useState('');
  const [forceRegen, setForceRegen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setError(null);
    setResult(null);
    if (!phone.trim()) { setError('Phone number ज़रूरी है'); return; }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/poocho-guruji/admin/recover-by-phone`,
        {
          phone: phone.trim(),
          override_phone: overridePhone.trim() || null,
          override_email: overrideEmail.trim() || null,
          force_regenerate: forceRegen,
        },
        { withCredentials: true }
      );
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Recovery failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0410] text-white pt-24 pb-16 px-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold mb-2" data-testid="recover-page-title">
          Poocho Guruji · Session Recovery
        </h1>
        <p className="text-sm text-white/60 mb-8">
          Admin utility — paying customer ke ₹99 session ko phone number se dhundh ke answer regenerate + WhatsApp/email par re-deliver karein.
        </p>

        <div className="space-y-4 bg-white/[0.03] border border-white/10 rounded-2xl p-5">
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">
              Customer's phone (form me diya hua) *
            </label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98765 43210"
              className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400"
              data-testid="recover-phone-input"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">
              Override WhatsApp target <span className="text-white/30 lowercase">(optional — agar customer ka phone Twilio FROM ke same ho)</span>
            </label>
            <input
              value={overridePhone}
              onChange={(e) => setOverridePhone(e.target.value)}
              placeholder="+91 XXXXX XXXXX"
              className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400"
              data-testid="recover-override-phone-input"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">
              Override email target <span className="text-white/30 lowercase">(optional)</span>
            </label>
            <input
              value={overrideEmail}
              onChange={(e) => setOverrideEmail(e.target.value)}
              placeholder="new@example.com"
              className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400"
              data-testid="recover-override-email-input"
            />
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={forceRegen}
              onChange={(e) => setForceRegen(e.target.checked)}
              data-testid="recover-force-regen"
            />
            <span>Force regenerate (LLM fresh answer — even if one already exists)</span>
          </label>

          <button
            onClick={run}
            disabled={loading}
            className="w-full bg-gradient-to-b from-amber-400 to-amber-600 text-black font-bold rounded-lg py-3 flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="recover-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {loading ? 'Recover ho raha hai...' : 'Recover & Deliver'}
          </button>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-500/40 bg-red-500/10 p-4 flex items-start gap-2 text-sm" data-testid="recover-error">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        {result && (
          <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-4 space-y-3" data-testid="recover-result">
            <div className="flex items-center gap-2 font-semibold text-emerald-300">
              <CheckCircle2 className="w-4 h-4" /> {result.action || 'Done'}
            </div>
            <div className="text-xs text-white/60">
              session_id: <code className="text-white/80">{result.session_id}</code>
            </div>
            {result.whatsapp && (
              <div className="flex items-start gap-2 text-sm" data-testid="recover-wa-status">
                <MessageCircle className="w-4 h-4 mt-0.5" />
                <div>
                  <div>WhatsApp: <b>{result.whatsapp.sent || 0}</b> sent, <b>{result.whatsapp.failed || 0}</b> failed</div>
                  {result.whatsapp.skipped_reason && (
                    <div className="text-amber-400 text-xs">Skipped: {result.whatsapp.skipped_reason}</div>
                  )}
                  {result.whatsapp.sids?.length > 0 && (
                    <div className="text-white/40 text-xs">SIDs: {result.whatsapp.sids.join(', ')}</div>
                  )}
                </div>
              </div>
            )}
            {result.email && (
              <div className="flex items-start gap-2 text-sm" data-testid="recover-em-status">
                <Mail className="w-4 h-4 mt-0.5" />
                <div>
                  Email: <b>{result.email.sent ? 'sent' : 'failed'}</b> {result.email.to && <>to <code>{result.email.to}</code></>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
